import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, Legend
} from 'recharts';
import { CountryData } from '../data/countryData';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import './EnhancedCharts.css';

interface EnhancedChartsProps {
  country: CountryData;
  darkMode: boolean;
}

const EnhancedCharts: React.FC<EnhancedChartsProps> = ({ country, darkMode }) => {
  const colors = {
    primary: '#4a90e2',
    success: '#2ecc71',
    warning: '#f39c12',
    error: '#e74c3c',
    info: '#3498db',
    purple: '#9b59b6',
    orange: '#e67e22',
    teal: '#1abc9c'
  };

  const textColor = darkMode ? '#ffffff' : '#333333';
  const gridColor = darkMode ? '#444444' : '#e0e0e0';

  // Cost breakdown data
  const costBreakdownData = Object.entries(country.livingCosts).map(([category, cost]) => ({
    name: category.charAt(0).toUpperCase() + category.slice(1),
    value: cost,
    percentage: (cost / Object.values(country.livingCosts).reduce((sum, c) => sum + c, 0) * 100).toFixed(1)
  }));

  // Quality of life radar data
  const qualityOfLifeData = [
    {
      subject: 'Safety',
      value: country.qualityOfLife.safetyIndex,
      fullMark: 10
    },
    {
      subject: 'Healthcare',
      value: country.qualityOfLife.healthcareIndex,
      fullMark: 10
    },
    {
      subject: 'Education',
      value: country.qualityOfLife.educationIndex,
      fullMark: 10
    },
    {
      subject: 'Environment',
      value: country.qualityOfLife.environmentIndex,
      fullMark: 10
    },
    {
      subject: 'Work-Life',
      value: country.marketStats.workLifeBalance,
      fullMark: 10
    },
    {
      subject: 'Job Security',
      value: country.marketStats.jobSecurity,
      fullMark: 10
    }
  ];

  // Top jobs growth data
  const jobGrowthData = country.topPayingJobs.map(job => ({
    title: job.title.length > 15 ? job.title.substring(0, 15) + '...' : job.title,
    salary: job.avgSalary / 1000,
    growth: job.growthRate,
    jobs: job.jobCount / 1000
  }));

  // Economic indicators over time (simulated data)
  const economicTrendData = [
    { year: '2019', gdp: country.economicIndicators.gdpPerCapita * 0.92, unemployment: country.economicIndicators.unemploymentRate + 1.2 },
    { year: '2020', gdp: country.economicIndicators.gdpPerCapita * 0.88, unemployment: country.economicIndicators.unemploymentRate + 2.1 },
    { year: '2021', gdp: country.economicIndicators.gdpPerCapita * 0.94, unemployment: country.economicIndicators.unemploymentRate + 1.5 },
    { year: '2022', gdp: country.economicIndicators.gdpPerCapita * 0.98, unemployment: country.economicIndicators.unemploymentRate + 0.8 },
    { year: '2023', gdp: country.economicIndicators.gdpPerCapita, unemployment: country.economicIndicators.unemploymentRate }
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className={`custom-tooltip ${darkMode ? 'dark' : 'light'}`}>
          <p className="label">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.value}${entry.dataKey === 'percentage' ? '%' : ''}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`enhanced-charts ${darkMode ? 'dark' : 'light'}`}>
      {/* Cost of Living Breakdown */}
      <div className="chart-container">
        <h4><AttachMoneyIcon style={{ marginRight: '8px', verticalAlign: 'middle', fontSize: '18px' }} /> Living Costs Breakdown</h4>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={costBreakdownData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={5}
              dataKey="value"
            >
              {costBreakdownData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={Object.values(colors)[index % Object.values(colors).length]} 
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Quality of Life Radar */}
      <div className="chart-container">
        <h4>🌟 Quality of Life Index</h4>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={qualityOfLifeData}>
            <PolarGrid stroke={gridColor} />
            <PolarAngleAxis dataKey="subject" tick={{ fill: textColor, fontSize: 12 }} />
            <PolarRadiusAxis 
              angle={30} 
              domain={[0, 10]} 
              tick={{ fill: textColor, fontSize: 10 }}
            />
            <Radar
              name="Score"
              dataKey="value"
              stroke={colors.primary}
              fill={colors.primary}
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Job Market Growth */}
      <div className="chart-container">
        <h4>📈 Top Jobs: Salary vs Growth</h4>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={jobGrowthData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis 
              dataKey="title" 
              tick={{ fill: textColor, fontSize: 10 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: textColor, fontSize: 12 }}
              label={{ value: 'Salary (k)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: textColor } }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              tick={{ fill: textColor, fontSize: 12 }}
              label={{ value: 'Growth %', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fill: textColor } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar yAxisId="left" dataKey="salary" fill={colors.primary} name="Avg Salary (k)" />
            <Line yAxisId="right" type="monotone" dataKey="growth" stroke={colors.success} strokeWidth={3} name="Growth Rate %" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Economic Trends */}
      <div className="chart-container">
        <h4><TrendingUpIcon style={{ marginRight: '8px', verticalAlign: 'middle', fontSize: '18px' }} /> Economic Trends (5 Years)</h4>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={economicTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="year" tick={{ fill: textColor }} />
            <YAxis 
              yAxisId="left"
              tick={{ fill: textColor }}
              label={{ value: 'GDP per Capita', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: textColor } }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              tick={{ fill: textColor }}
              label={{ value: 'Unemployment %', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fill: textColor } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="gdp" 
              stroke={colors.primary} 
              fill={colors.primary}
              fillOpacity={0.3}
              name="GDP per Capita"
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="unemployment" 
              stroke={colors.error} 
              strokeWidth={3}
              name="Unemployment Rate"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Top Employers */}
      <div className="chart-container">
        <h4><BusinessIcon style={{ marginRight: '8px', verticalAlign: 'middle', fontSize: '18px' }} /> Top Employers by Jobs & Salary</h4>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={country.topEmployers.map(emp => ({
              name: emp.name,
              salary: emp.avgSalary / 1000,
              jobs: emp.jobCount / 100
            }))}
            layout="horizontal"
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis type="number" tick={{ fill: textColor }} />
            <YAxis dataKey="name" type="category" tick={{ fill: textColor, fontSize: 12 }} width={80} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="salary" fill={colors.success} name="Avg Salary (k)" />
            <Bar dataKey="jobs" fill={colors.info} name="Jobs (hundreds)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EnhancedCharts;